import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { dateFormat } from '@bloomays-lib/utils.shared';
import { ApolloGqlTalentForTalentBoard } from '@bloomays-lib/types.shared';
import emptyImage from '../../assets/empty.svg';
import Button from '../atoms/Button';
import EmptyState from '../molecules/Emptystate';
import TalentSkillsSection from './TalentSkillsSection';
import TalentExperiencesSection from './TalentExperiencesSection';
import TalentFormationsSection from './TalentFormationsSection';
import TalentLanguagesSection from './TalentLanguagesSection';

type TalentProfileProps = {
  talent: ApolloGqlTalentForTalentBoard;
  contentTab: 'cv' | 'experience' | 'formation';
  setContentTab: (contentTab: 'cv' | 'experience' | 'formation') => void;
};

const TalentProfile = ({ talent, contentTab, setContentTab }: TalentProfileProps) => {
  const { t } = useTranslation('talentBoard');

  return (
    <>
      <ContentTabButtonWrapper>
        <Button
          textButton={t('talentJob.cv')}
          onClick={() => setContentTab('cv')}
          sx={{ background: contentTab === 'cv' ? '' : 'transparent' }}
        />
        <Button
          textButton={t('talentJob.experience')}
          onClick={() => setContentTab('experience')}
          sx={{ background: contentTab === 'experience' ? '' : 'transparent' }}
        />
        <Button
          textButton={t('talentJob.formation')}
          onClick={() => setContentTab('formation')}
          sx={{ background: contentTab === 'formation' ? '' : 'transparent' }}
        />
      </ContentTabButtonWrapper>
      <ContentContainer>
        {contentTab === 'cv' && (
          <EmptyStateContainer>
            <EmptyState image={emptyImage} text={t('talentJob.emptyCV')} />
          </EmptyStateContainer>
        )}
        {contentTab === 'experience' && (
          <>
            <TalentSkillsSection
              skills={
                talent.skillLevels?.map((skillLevel) => ({
                  id: skillLevel.id ?? '',
                  skill: {
                    id: skillLevel.skill.id,
                    label: skillLevel.skill.label,
                    iso: skillLevel.skill.iso,
                    alternateLabels: skillLevel.skill.alternateLabels,
                  },
                  level: skillLevel.level ?? undefined,
                })) || []
              }
            />
            <TalentExperiencesSection
              jobs={
                talent.jobs?.map((job) => ({
                  id: job.id ?? '',
                  jobTitle: job.jobTitle?.map((jt) => ({
                    id: jt.id,
                    label: jt.label,
                    iso: jt.iso ?? undefined,
                  })),
                  period: job.period
                    ? {
                        id: job.period.id,
                        startDate: dateFormat(job.period.startDate, 'yyyy-MM-dd'),
                        endDate: job.period.endDate ? dateFormat(job.period.endDate, 'yyyy-MM-dd') : undefined,
                      }
                    : undefined,
                  current: job.current ?? undefined,
                  label: job.label ?? undefined,
                  description: job.description ?? undefined,
                  name: job.name ?? undefined,
                  skills: job.skills?.map((skill) => ({
                    id: skill.id,
                    label: skill.label,
                    iso: skill.iso,
                    alternateLabels: skill.alternateLabels,
                  })),
                })) || []
              }
            />
          </>
        )}
        {contentTab === 'formation' && (
          <>
            <TalentFormationsSection
              educations={
                talent.educations?.map((education) => ({
                  id: education.id ?? '',
                  name: education.name,
                  label: education.label ?? undefined,
                  description: education.description ?? undefined,
                  current: education.current ?? undefined,
                  url: education.url ?? undefined,
                  type: education.type ?? undefined,
                  period: education.period
                    ? {
                        id: education.period.id,
                        startDate: dateFormat(education.period.startDate, 'yyyy-MM-dd'),
                        endDate: education.period.endDate
                          ? dateFormat(education.period.endDate, 'yyyy-MM-dd')
                          : undefined,
                      }
                    : undefined,
                })) || []
              }
            />
            <TalentLanguagesSection
              languages={
                talent.languages?.map((language) => ({
                  id: language.id ?? '',
                  label: language.label,
                  level: language.level ?? undefined,
                })) || []
              }
            />
          </>
        )}
      </ContentContainer>
    </>
  ) as React.ReactNode;
};

const ContentTabButtonWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  padding: '0 12px',
  [theme.breakpoints.down('md')]: {
    gap: '8px',
    paddingBottom: '24px',
    backgroundColor: theme.palette.neutral[10],
    justifyContent: 'center',
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  padding: '24px',
  [theme.breakpoints.down('md')]: {
    padding: '16px 0px',
  },
}));

const EmptyStateContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '160px',
  [theme.breakpoints.down('md')]: {
    marginTop: '60px',
  },
}));

export { TalentProfile };
export default TalentProfile;
